import {
  useEffect,
  useRef,
  useState,
  type AnchorHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
} from "react";

type HeadingLevel = 1 | 2 | 3;
type HeadingSize = "lg" | "md" | "sm";

function classes(base: string[], className: string | undefined): string {
  if (className) {
    base.push(className);
  }

  return base.join(" ");
}

export interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  children: ReactNode;
  level?: HeadingLevel;
  size?: HeadingSize;
}

export function Heading({
  children,
  className,
  level = 2,
  size,
  ...props
}: HeadingProps) {
  const resolvedSize = size ?? (level === 1 ? "lg" : level === 2 ? "md" : "sm");
  const headingClasses = classes(
    ["marker-heading", `marker-heading--${resolvedSize}`],
    className,
  );

  if (level === 1) {
    return (
      <h1 className={headingClasses} {...props}>
        {children}
      </h1>
    );
  }

  if (level === 3) {
    return (
      <h3 className={headingClasses} {...props}>
        {children}
      </h3>
    );
  }

  return (
    <h2 className={headingClasses} {...props}>
      {children}
    </h2>
  );
}

type TextVariant = "body" | "small" | "muted" | "caps" | "masked";
type TextElement = "p" | "span";

type TruncationProps =
  | { children: ReactNode; title?: string; truncate?: false }
  | { children: string; title?: string; truncate: true }
  | { children: ReactNode; title: string; truncate: boolean };

export type TextProps = Omit<
  HTMLAttributes<HTMLElement>,
  "children" | "title"
> &
  TruncationProps & {
    as?: TextElement;
    variant?: TextVariant;
  };

export function Text({
  as = "p",
  children,
  className,
  title,
  truncate = false,
  variant = "body",
  ...props
}: TextProps) {
  const [isRevealed, setIsRevealed] = useState(false);
  const maskedValueRef = useRef<HTMLSpanElement>(null);
  const isMasked = variant === "masked";
  useEffect(() => {
    if (!isMasked || !isRevealed) return;
    maskedValueRef.current
      ?.querySelector<HTMLElement>("input, select, textarea")
      ?.focus();
  }, [isMasked, isRevealed]);
  const textClasses = classes(
    [
      "marker-text",
      `marker-text--${variant}`,
      ...(truncate ? ["marker-text--truncate"] : []),
    ],
    className,
  );
  const resolvedTitle =
    isMasked && !isRevealed
      ? undefined
      : (title ??
        (truncate && typeof children === "string" ? children : undefined));
  const renderedChildren = isMasked ? (
    <>
      <span className="marker-text__masked-value" ref={maskedValueRef}>
        {isRevealed ? children : "••••••••"}
      </span>{" "}
      <button
        aria-expanded={isRevealed}
        className="marker-text__mask-toggle"
        onClick={() => {
          setIsRevealed((current) => !current);
        }}
        type="button"
      >
        {isRevealed ? "Hide" : "Reveal"}
      </button>
    </>
  ) : (
    children
  );

  if (as === "span") {
    return (
      <span className={textClasses} title={resolvedTitle} {...props}>
        {renderedChildren}
      </span>
    );
  }

  return (
    <p className={textClasses} title={resolvedTitle} {...props}>
      {renderedChildren}
    </p>
  );
}

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  children: ReactNode;
}

export function Link({ children, className, ...props }: LinkProps) {
  return (
    <a className={classes(["marker-link"], className)} {...props}>
      {children}
    </a>
  );
}

export type CodeProps = Omit<
  HTMLAttributes<HTMLElement>,
  "children" | "title"
> &
  TruncationProps & {
    block?: boolean;
  };

export function Code({
  block = false,
  children,
  className,
  title,
  truncate = false,
  ...props
}: CodeProps) {
  const codeClasses = ["marker-code"];
  if (block) {
    codeClasses.push("marker-code--block");
  }
  if (truncate) {
    codeClasses.push("marker-code--truncate");
  }
  const resolvedTitle =
    title ?? (truncate && typeof children === "string" ? children : undefined);

  return (
    <code
      className={classes(codeClasses, className)}
      title={resolvedTitle}
      {...props}
    >
      {children}
    </code>
  );
}
